<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import type { iconName } from "@/Modules/App/Models/Styles/Icons";

import TcIcon from "@/Components/Extend/TcIcon.vue";

import { isObject } from "@/Utils/Objects";

export interface TcSnackbarProps {
  message: string;
  messageParameters?: Record<string, unknown>;
  type?: NotificationType;
}

const { t } = useI18n();

const props = withDefaults(defineProps<TcSnackbarProps>(), { type: NotificationType.INFO });

const icon = computed((): iconName => {
  return props.type === NotificationType.INFO
    ? "InfoCircle"
    : props.type === NotificationType.ERROR
      ? "WarningCircle"
      : "DoubleCheck";
});
</script>

<template>
  <div id="snackbar" :class="`${props.message ? 'show' : ''} ${props.type}`">
    <div class="icon">
      <TcIcon :name="icon" size="md" :color="`semantic-${props.type}`" />
    </div>

    <div>
      <span v-if="message">
        {{ t(props.message, isObject(messageParameters) ? messageParameters : {}) }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#snackbar {
  @include elevation(l);
  @include sp.padding(false, 2);
  @include radius(s);
  @include text.general(low, l);

  position: fixed;
  bottom: calc($space-5 + $size-navigation-height-mobile + var(--ion-safe-area-bottom));
  left: $space-5;
  z-index: 100001;
  opacity: 0;
  transition: all ease-in-out 0.5s;
  visibility: hidden;
  width: calc(100% - $space-10);
  gap: $space-4;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  @media (min-width: $breakpoint-m) {
    max-width: 320px;
    left: calc($space-5 + $size-navigation-width);
    bottom: $space-5;
  }

  .icon {
    @include radius(xxl);

    display: flex;
    align-items: center;
    justify-content: center;
    width: $space-12;
    height: $space-12;
    flex: none;
  }

  &.info {
    @include border(2, solid, b);
    @include colors.border(semantic, info, true);
    @include colors.bg(semantic, info-low, true);
  }

  &.error {
    @include border(2, solid, b);
    @include colors.border(semantic, error, true);
    @include colors.bg(semantic, error-low, true);
  }

  &.success {
    @include border(2, solid, b);
    @include colors.border(semantic, success, true);
    @include colors.bg(semantic, success-low, true);
  }

  &.show {
    opacity: 1;
    visibility: visible;
  }
}
</style>
